import { apiPost } from '../config/api';

function mapCortiSectionsToNote(rawResponse, noteType) {
  const sections = rawResponse?.sections || rawResponse?.document?.sections || [];

  const sectionMap = {};
  sections.forEach((section) => {
    const key = (section.key || section.name || '').toLowerCase();
    const text = section.text || section.content || section.markdown || '';
    sectionMap[key] = text;
  });

  const datetime = new Date().toISOString();
  const emptyPatient = {
    name: null,
    room: null,
    age: null,
    sex: null,
    details_source: 'transcript',
  };

  if (noteType === 'handover') {
    return {
      note_type: 'handover',
      patient: emptyPatient,
      datetime,
      sbar: {
        situation: sectionMap.situation || sectionMap.subjective || sections[0]?.text || '',
        background: sectionMap.background || sectionMap.objective || sections[1]?.text || '',
        assessment: sectionMap.assessment || sections[2]?.text || '',
        recommendation: sectionMap.recommendation || sectionMap.plan || sections[3]?.text || '',
      },
      outstanding_tasks: [],
      negations_detected: [],
      flags: ['Generated by Corti — please verify all sections'],
      _cortiRaw: rawResponse,
    };
  }

  if (noteType === 'vitals') {
    return {
      note_type: 'vitals',
      patient: emptyPatient,
      datetime,
      vitals: {
        bp_systolic: null,
        bp_diastolic: null,
        pulse: null,
        temp_celsius: null,
        o2_sat: null,
        rr: null,
        pain: null,
        weight_kg: null,
        blood_sugar: null,
      },
      flags: ['Corti vitals extraction — please verify values'],
      _cortiRaw: rawResponse,
      _cortiSections: sections,
    };
  }

  return {
    note_type: 'progress_note',
    patient: emptyPatient,
    datetime,
    soap: {
      subjective:
        sectionMap.subjective ||
        sections.find((s) => /subjective/i.test(s.key || s.name || ''))?.text ||
        sections[0]?.text ||
        '',
      objective:
        sectionMap.objective ||
        sections.find((s) => /objective/i.test(s.key || s.name || ''))?.text ||
        sections[1]?.text ||
        '',
      assessment:
        sectionMap.assessment ||
        sections.find((s) => /assessment/i.test(s.key || s.name || ''))?.text ||
        sections[2]?.text ||
        '',
      plan:
        sectionMap.plan ||
        sections.find((s) => /plan/i.test(s.key || s.name || ''))?.text ||
        sections[3]?.text ||
        '',
    },
    vitals_extracted: {
      bp: null,
      pulse: null,
      temp_celsius: null,
      o2_sat: null,
      rr: null,
      pain: null,
    },
    negations_detected: [],
    flags: ['Generated by Corti — please verify all sections'],
    _cortiRaw: rawResponse,
  };
}

export async function generateCortiNote(transcript, noteType) {
  if (noteType === 'vitals') {
    return { note: null, raw: null, failed: true, reason: 'Corti template not configured for vitals notes' };
  }

  if (noteType === 'medication_admin') {
    return {
      note: null,
      raw: null,
      failed: true,
      reason: 'Corti template not configured for medication admin notes',
    };
  }

  try {
    const data = await apiPost('/api/corti/generate', { transcript, noteType });

    if (data.failed) {
      return { note: null, raw: null, failed: true, reason: data.reason || 'Corti generation failed' };
    }

    const note = mapCortiSectionsToNote(data.raw, noteType);
    return { note, raw: data.raw, failed: false };
  } catch (error) {
    console.error('Corti generation failed:', error);
    return { note: null, raw: null, failed: true, reason: error.message };
  }
}

export function getCortiDisplaySections(cortiNote, cortiRaw) {
  if (cortiRaw?.sections) {
    return cortiRaw.sections.map((s) => ({
      title: s.name || s.key || 'Section',
      content: s.text || s.content || s.markdown || '',
    }));
  }

  if (cortiNote?._cortiSections) {
    return cortiNote._cortiSections.map((s) => ({
      title: s.name || s.key || 'Section',
      content: s.text || s.content || '',
    }));
  }

  return [];
}
